import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { StyledCol, StyledRow } from './styles/grid.style';

// --- Constants ---

const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

const RESPONSIVE_ARRAY: Breakpoint[] = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
const BREAKPOINT_KEYS: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
const PREFIX_ROW = 'ant-row';
const PREFIX_COL = 'ant-col';
const ANTISH = 'antish';

// --- Types ---

type ColSpanType = number | undefined;

interface ColSize {
  span?: ColSpanType;
  offset?: ColSpanType;
  order?: ColSpanType;
  push?: ColSpanType;
  pull?: ColSpanType;
  flex?: number | string;
}

type RowAlign = 'top' | 'middle' | 'bottom' | 'stretch';
type RowJustify = 'start' | 'end' | 'center' | 'space-around' | 'space-between' | 'space-evenly';
type ResponsiveValue<T> = T | Partial<Record<Breakpoint, T>>;

type GutterValue = number | string;
type ResponsiveGutter = Partial<Record<Breakpoint, GutterValue>>;
type Gutter =
  | GutterValue
  | ResponsiveGutter
  | [GutterValue | ResponsiveGutter, GutterValue | ResponsiveGutter];

export interface RowProps extends React.HTMLAttributes<HTMLDivElement> {
  gutter?: Gutter;
  align?: ResponsiveValue<RowAlign>;
  justify?: ResponsiveValue<RowJustify>;
  wrap?: boolean;
}

export interface ColProps extends React.HTMLAttributes<HTMLDivElement> {
  span?: ColSpanType;
  offset?: ColSpanType;
  order?: ColSpanType;
  push?: ColSpanType;
  pull?: ColSpanType;
  flex?: number | string;
  xs?: ColSpanType | ColSize;
  sm?: ColSpanType | ColSize;
  md?: ColSpanType | ColSize;
  lg?: ColSpanType | ColSize;
  xl?: ColSpanType | ColSize;
  xxl?: ColSpanType | ColSize;
}

// --- Context ---

interface RowContextValue {
  gutter: [number, number];
  wrap: boolean;
}

const RowContext = createContext<RowContextValue>({
  gutter: [0, 0],
  wrap: true,
});

// --- useBreakpoint Hook ---

type ScreenMap = Record<Breakpoint, boolean>;

function getInitialScreens(): ScreenMap {
  if (typeof window === 'undefined') {
    return { xs: true, sm: false, md: false, lg: false, xl: false, xxl: false };
  }
  const w = window.innerWidth;
  return {
    xs: w >= BREAKPOINTS.xs,
    sm: w >= BREAKPOINTS.sm,
    md: w >= BREAKPOINTS.md,
    lg: w >= BREAKPOINTS.lg,
    xl: w >= BREAKPOINTS.xl,
    xxl: w >= BREAKPOINTS.xxl,
  };
}

function useBreakpoint(): ScreenMap {
  const [screens, setScreens] = useState<ScreenMap>(getInitialScreens);

  useEffect(() => {
    const cleanups: Array<() => void> = [];

    for (const key of BREAKPOINT_KEYS) {
      const minWidth = BREAKPOINTS[key];
      if (minWidth === 0) continue;

      const mql = window.matchMedia(`(min-width: ${minWidth}px)`);
      const handler = (e: MediaQueryListEvent) => {
        setScreens((prev) => ({ ...prev, [key]: e.matches }));
      };
      mql.addEventListener('change', handler);
      cleanups.push(() => mql.removeEventListener('change', handler));
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return screens;
}

// --- Helpers ---

function resolveResponsive<T>(
  value: T | Partial<Record<Breakpoint, T>>,
  screens: ScreenMap
): T | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== 'object' || Array.isArray(value)) return value as T;

  const obj = value as Partial<Record<Breakpoint, T>>;
  for (const bp of RESPONSIVE_ARRAY) {
    if (screens[bp] && obj[bp] !== undefined) {
      return obj[bp];
    }
  }
  return undefined;
}

function resolveGutterValue(value: GutterValue | ResponsiveGutter, screens: ScreenMap): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value) || 0;
  // Responsive object
  const resolved = resolveResponsive<GutterValue>(value, screens);
  if (resolved === undefined) return 0;
  return typeof resolved === 'number' ? resolved : parseFloat(resolved) || 0;
}

function resolveGutter(gutter: Gutter | undefined, screens: ScreenMap): [number, number] {
  if (gutter === undefined || gutter === null) return [0, 0];

  if (Array.isArray(gutter)) {
    return [resolveGutterValue(gutter[0], screens), resolveGutterValue(gutter[1], screens)];
  }

  return [resolveGutterValue(gutter as GutterValue | ResponsiveGutter, screens), 0];
}

function parseFlex(flex: number | string): string {
  if (typeof flex === 'number') {
    return `${flex} ${flex} auto`;
  }
  if (flex === 'auto') return '1 1 auto';
  if (flex === 'none') return 'none';
  if (/^\d+(\.\d+)?$/.test(flex)) {
    return `${flex} ${flex} auto`;
  }
  return `0 0 ${flex}`;
}

function buildSizeCss(size: ColSize, flexProp?: number | string): string {
  let result = '';
  const { span, offset, order, push, pull, flex: sizeFlex } = size;
  const effectiveFlex = sizeFlex ?? flexProp;

  if (span !== undefined) {
    if (span === 0) {
      result += 'display: none;';
    } else {
      const pct = `${(span / 24) * 100}%`;
      result += `display: block; flex: 0 0 ${pct}; max-width: ${pct};`;
    }
  }

  if (effectiveFlex !== undefined) {
    result += `flex: ${parseFlex(effectiveFlex)};`;
    // When flex is set, remove max-width constraint
    if (span === undefined) {
      result += 'max-width: none;';
    }
  }

  if (offset !== undefined && offset > 0) {
    result += `margin-left: ${(offset / 24) * 100}%;`;
  }

  if (order !== undefined) {
    result += `order: ${order};`;
  }

  if (push !== undefined && push > 0) {
    result += `left: ${(push / 24) * 100}%;`;
  }

  if (pull !== undefined && pull > 0) {
    result += `right: ${(pull / 24) * 100}%;`;
  }

  return result;
}

function buildColCss(props: ColProps): string {
  const { span, offset, order, push, pull, flex, xs, sm, md, lg, xl, xxl } = props;
  let result = '';

  // Base (non-responsive) props
  const baseSize: ColSize = { span, offset, order, push, pull };
  const baseCss = buildSizeCss(baseSize, flex);
  if (baseCss) {
    result += baseCss;
  }

  // Responsive breakpoint props
  const responsiveProps: Record<Breakpoint, ColSpanType | ColSize | undefined> = {
    xs,
    sm,
    md,
    lg,
    xl,
    xxl,
  };

  for (const bp of BREAKPOINT_KEYS) {
    const bpValue = responsiveProps[bp];
    if (bpValue === undefined) continue;

    const size: ColSize = typeof bpValue === 'number' ? { span: bpValue } : bpValue;
    const sizeCss = buildSizeCss(size);
    if (!sizeCss) continue;

    if (bp === 'xs') {
      // xs has no media query (mobile-first base)
      result += sizeCss;
    } else {
      result += `@media (min-width: ${BREAKPOINTS[bp]}px) { ${sizeCss} }`;
    }
  }

  return result;
}

// --- ClassName Builders ---

function cx(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

function buildRowClassName(
  align: ResponsiveValue<RowAlign> | undefined,
  justify: ResponsiveValue<RowJustify> | undefined,
  wrap: boolean,
  screens: ScreenMap,
  userClassName?: string
): string {
  const resolvedAlign =
    align !== undefined
      ? typeof align === 'string'
        ? align
        : resolveResponsive(align, screens)
      : undefined;
  const resolvedJustify =
    justify !== undefined
      ? typeof justify === 'string'
        ? justify
        : resolveResponsive(justify, screens)
      : undefined;

  return cx(
    ANTISH,
    PREFIX_ROW,
    resolvedAlign && `${PREFIX_ROW}-${resolvedAlign}`,
    resolvedJustify && `${PREFIX_ROW}-${resolvedJustify}`,
    !wrap && `${PREFIX_ROW}-no-wrap`,
    userClassName
  );
}

function addSizeClasses(classes: string[], size: ColSize, prefix: string) {
  if (size.span !== undefined) classes.push(`${prefix}-${size.span}`);
  if (size.offset !== undefined && size.offset > 0) classes.push(`${prefix}-offset-${size.offset}`);
  if (size.order !== undefined) classes.push(`${prefix}-order-${size.order}`);
  if (size.push !== undefined && size.push > 0) classes.push(`${prefix}-push-${size.push}`);
  if (size.pull !== undefined && size.pull > 0) classes.push(`${prefix}-pull-${size.pull}`);
}

function buildColClassName(props: ColProps, userClassName?: string): string {
  const { span, offset, order, push, pull, xs, sm, md, lg, xl, xxl } = props;
  const classes: string[] = [ANTISH, PREFIX_COL];

  // Base props
  if (span !== undefined) classes.push(`${PREFIX_COL}-${span}`);
  if (offset !== undefined && offset > 0) classes.push(`${PREFIX_COL}-offset-${offset}`);
  if (order !== undefined) classes.push(`${PREFIX_COL}-order-${order}`);
  if (push !== undefined && push > 0) classes.push(`${PREFIX_COL}-push-${push}`);
  if (pull !== undefined && pull > 0) classes.push(`${PREFIX_COL}-pull-${pull}`);

  // Responsive breakpoint classes
  const responsiveProps: Record<Breakpoint, ColSpanType | ColSize | undefined> = {
    xs,
    sm,
    md,
    lg,
    xl,
    xxl,
  };

  for (const bp of BREAKPOINT_KEYS) {
    const bpValue = responsiveProps[bp];
    if (bpValue === undefined) continue;

    if (typeof bpValue === 'number') {
      classes.push(`${PREFIX_COL}-${bp}-${bpValue}`);
    } else {
      addSizeClasses(classes, bpValue, `${PREFIX_COL}-${bp}`);
    }
  }

  if (userClassName) classes.push(userClassName);
  return classes.join(' ');
}

// --- Row Component ---

export const Row = React.forwardRef<HTMLDivElement, RowProps>(
  ({ gutter = 0, align, justify, wrap = true, className, children, ...rest }, ref) => {
    const screens = useBreakpoint();
    const [hGutter, vGutter] = resolveGutter(gutter, screens);

    // Prepare align/justify for styled component (pass as-is if string, resolve if responsive)
    const resolvedAlign =
      align !== undefined ? (typeof align === 'string' ? align : align) : undefined;
    const resolvedJustify =
      justify !== undefined ? (typeof justify === 'string' ? justify : justify) : undefined;

    const contextValue = useMemo<RowContextValue>(
      () => ({ gutter: [hGutter, vGutter], wrap }),
      [hGutter, vGutter, wrap]
    );

    const mergedClassName = buildRowClassName(align, justify, wrap, screens, className);

    return (
      <RowContext.Provider value={contextValue}>
        <StyledRow
          ref={ref}
          className={mergedClassName}
          $hGutter={hGutter}
          $vGutter={vGutter}
          $wrap={wrap}
          $align={resolvedAlign as any}
          $justify={resolvedJustify as any}
          {...rest}
        >
          {children}
        </StyledRow>
      </RowContext.Provider>
    );
  }
);

Row.displayName = 'Row';

// --- Col Component ---

export const Col = React.forwardRef<HTMLDivElement, ColProps>(
  (
    {
      span,
      offset,
      order,
      push,
      pull,
      flex,
      xs,
      sm,
      md,
      lg,
      xl,
      xxl,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const { gutter } = useContext(RowContext);
    const [hGutter] = gutter;

    const colCss = useMemo(
      () => buildColCss({ span, offset, order, push, pull, flex, xs, sm, md, lg, xl, xxl }),
      [span, offset, order, push, pull, flex, xs, sm, md, lg, xl, xxl]
    );

    const mergedClassName = buildColClassName(
      { span, offset, order, push, pull, flex, xs, sm, md, lg, xl, xxl },
      className
    );

    return (
      <StyledCol
        ref={ref}
        className={mergedClassName}
        $hGutter={hGutter}
        $colCss={colCss}
        {...rest}
      >
        {children}
      </StyledCol>
    );
  }
);

Col.displayName = 'Col';
